import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cachedGet, mutateOrQueue } from '../../lib/api-client';
import { invalidate } from '../../lib/cache';
import { Spinner } from '../../components/kit/Spinner';
import { ErrorState } from '../../components/kit/ErrorState';
import { Card } from '../../components/kit/Card';
import { Button } from '../../components/kit/Button';
import { StatusBadge } from '../../components/kit/StatusBadge';
import { StaleBanner } from '../../components/app/StaleBanner';
import { useSetPageHeader } from '../../components/app/AppShell';
import { MapHero } from '../../components/map/MapHero';
import { TripProgressBar } from '../../components/trip/TripProgressBar';
import { StartTripSheet } from '../../components/trip/StartTripSheet';
import { ExpenseSheet, type ExpenseDraft } from '../../components/trip/ExpenseSheet';
import { ReleaseDutySlipSheet } from '../../components/trip/ReleaseDutySlipSheet';
import { EndReadingSheet } from '../../components/trip/EndReadingSheet';
import { ReleaseReadingCard } from '../../components/trip/ReleaseReadingCard';
import { discardPendingMutationsForBooking, pendingMutations, type QueueItem } from '../../lib/sync/queue';
import { formatISTDate, formatISTDateTime, formatISTTime, formatINR, istDateYmd } from '../../lib/format';
import { haptic } from '../../lib/haptic';
import { geocodeAddress, type GeoCoords } from '../../lib/geocode';
import { useWakeLock } from '../../lib/useWakeLock';
import { onTripRefresh } from '../../lib/trip-refresh';

/**
 * Detail row for a single booking. The optional coordinate + notes fields come
 * directly off the Prisma Booking row (the API returns it via findUnique).
 *
 * Note: the schema names are `dropoffLat / dropoffLng` (NOT `dropLat / dropLng`).
 * Earlier task drafts said `drop*`; we use `dropoff*` so the map renders against
 * live data.
 */
type TripDetail = {
  id: string;
  /** Human-readable booking code, e.g. `FL26-SC-3821`. Used in URLs and
   *  rendered next to the status pill at the top of the detail screen. */
  code?: string | null;
  status: string;
  assignmentVersion?: number;
  pickupTime: string;
  pickupLocation?: string | null;
  dropLocation?: string | null;
  /** Passenger's flight / train, shown as a chip under Pickup. From
   *  Booking.flightNo / Booking.trainNo (already returned by the driver DTO). */
  flightNo?: string | null;
  trainNo?: string | null;
  client?: { name: string } | null;
  passengers?: { id: string; name: string; phone: string }[];
  activeVehicle?: { regNumber: string; make: string; model: string } | null;
  // Backend-computed window flag: true while the trip is live + for 24h after
  // release, then false (locked for billing). Drives the add/edit/delete UI.
  expensesEditable?: boolean;
  endReadingEditable?: boolean;
  expenses?: {
    id: string; type: string; amount: number; label?: string | null;
    date?: string | null; description?: string | null;
    paymentSource?: string | null; receiptUrl?: string | null;
    externalProvider?: string | null;
    reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
    rejectionReason?: string | null;
    pendingSync?: boolean;
  }[];
  /** Set once the passenger duty-slip sign-off is captured. Drives the
   *  Duty Slip card's captured-vs-pending state. */
  signedOn?: string | null;
  dutySlipUrl?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropoffLat?: number | null;
  dropoffLng?: number | null;
  /** Operator-provided remarks (`Booking.remarks` on the backend). This is
   *  the driver-facing field shown on the connect.fleetup.in edit form as
   *  "Remarks". The sibling `Booking.notes` column is operator-internal and
   *  is intentionally stripped from driver-app responses — don't add it back
   *  here. */
  remarks?: string | null;
  // Booking metadata surfaced on the v2 route card's "4hr · 40km · Sedan" row.
  // The backend already returns these from the Booking row; we just type them
  // here so the frontend can read them without `as any`.
  dutyType?: string | null;
  requestedCarType?: string | null;
  bookingCategory?: string | null;
  // Trip log fields — populated as the driver progresses through the trip.
  // Used for the inline 4-cell grid (Garage / Start KM / Release / Release KM)
  // so the driver can see what they captured without diving into a sheet.
  startKm?: number | null;
  endKm?: number | null;
  actualStartDate?: string | null;
  actualStartTime?: string | null;
  actualEndDate?: string | null;
  actualEndTime?: string | null;
  releaseDate?: string | null;
  releaseTime?: string | null;
  releaseKm?: number | null;
  tripMeta?: { reachedLocationAt?: string | null; rideStartedAt?: string | null } | null;
};

/**
 * Amber mid-trip banner for a skipped start odometer: visible warning + inline
 * numeric entry, so the gap is fixable on the spot (2026-07-02 P0 — the old
 * flow only revealed the missing KM at completion, with no way back).
 */
function StartKmBanner({ submitting, onSave }: { submitting?: boolean; onSave: (km: number) => void }) {
  const { t } = useTranslation('trip');
  const [km, setKm] = useState('');
  const isValid = /^\d{3,6}$/.test(km);
  return (
    <div
      className="mx-6 mb-4 p-4 rounded-[14px]"
      style={{
        background: 'var(--status-warning-bg,#FBF1DE)',
        border: '1px solid rgba(197, 142, 58, 0.35)',
        borderLeft: '4px solid var(--status-warning-fg,#8A5E1D)',
      }}
      data-testid="start-km-banner"
    >
      <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--status-warning-fg,#8A5E1D)' }}>
        {t('startKmMissingEyebrow', { defaultValue: 'Start KM missing' })}
      </div>
      <p className="text-sm font-semibold text-[var(--color-text)] mt-1 mb-3">
        {t('startKmMissingBody', { defaultValue: 'Add the odometer reading from when you left the garage.' })}
      </p>
      <div className="flex gap-2">
        <input
          type="number"
          inputMode="numeric"
          value={km}
          onChange={(e) => setKm(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          className="flex-1 min-w-0 px-3 py-2.5 text-lg text-center font-mono tracking-widest bg-[var(--color-surface)] border-2 border-[var(--color-border,#d1d5db)] rounded-xl focus:border-[var(--green-700,#1B6A41)] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          disabled={!isValid || submitting}
          onClick={() => { haptic.select(); onSave(parseInt(km, 10)); }}
          className="px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
          style={{ background: 'var(--color-btn-primary,#1B6A41)' }}
        >
          {t('startKmMissingSave', { defaultValue: 'Save' })}
        </button>
      </div>
    </div>
  );
}

/**
 * Read-only textarea that renders multi-line text but stays selectable for
 * copy. Used for operator-provided strings (remarks, pickup, drop addresses)
 * so drivers can long-press → Copy on iOS / Android. `readOnly` keeps the
 * mobile keyboard from popping; auto-resize keeps the height pinned to the
 * content so the surrounding card doesn't grow a scrollbar.
 */
function SelectableText({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to a minimal height first so shrinking content collapses correctly,
    // then set to scrollHeight which reflects the wrapped line count.
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      readOnly
      value={value}
      aria-readonly
      rows={1}
      className={className}
      style={{
        width: '100%',
        background: 'transparent',
        border: 0,
        padding: 0,
        margin: 0,
        resize: 'none',
        outline: 'none',
        color: 'inherit',
        font: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
        overflow: 'hidden',
      }}
    />
  );
}

export function TripDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trip');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState<TripDetail | null>(null);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [activeSheet, setActiveSheet] = useState<'start' | 'expense' | 'dutyslip' | 'dutyslip-release' | 'end-reading' | null>(null);
  // Release captures TIME ONLY at the slide moment (2026-07-02 lifecycle); the
  // odometer + location follow on the post-signature close-out card.
  const [releaseDraft, setReleaseDraft] = useState<{ releaseDate: string; releaseTime: string } | null>(null);
  // Offline/unsynced mutations for THIS booking, overlaid onto server state so
  // a driver who started/advanced the trip on a dead network still sees the
  // progress after an app restart (the write is durable in IDB).
  const [pendingOps, setPendingOps] = useState<QueueItem[]>([]);
  // Expense being edited via the ExpenseSheet (null = add mode).
  const [editingExpense, setEditingExpense] = useState<NonNullable<TripDetail['expenses']>[number] | null>(null);
  // Client-side geocoded coords for trips where the booking has only address
  // strings. Looked up + cached in localStorage via geocodeAddress(). Null
  // until the lookup resolves (or fails).
  const [geocodedPickup, setGeocodedPickup] = useState<GeoCoords | null>(null);
  const [geocodedDrop, setGeocodedDrop] = useState<GeoCoords | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const load = async (forceNetwork = false) => {
    if (!id) return;
    let loadedTrip: TripDetail | null = null;
    setError(false); setLoading(true);
    try {
      const result = await cachedGet<TripDetail>(`/driver-app/me/trips/${id}`, { forceNetwork });
      loadedTrip = result.data;
      setTrip(result.data);
      setFetchedAt(Date.now());
      setStale(result.state === 'stale');
    } catch (e: any) {
      if (e?.status === 403) {
        await invalidate(`/driver-app/me/trips/${id}`);
        await discardPendingMutationsForBooking(id);
        navigate('/today', { replace: true });
        return;
      }
      setError(true);
    }
    finally { setLoading(false); }
    // Refresh the unsynced-mutation overlay alongside every trip load. URLs
    // are built from the same :id route param, so a prefix match is exact.
    try {
      const all = await pendingMutations();
      setPendingOps(all.filter(item => {
        if (!item.url.includes(`/bookings/${id}/`)) return false;
        const queuedVersion = (item.body as { assignmentVersion?: number } | undefined)?.assignmentVersion;
        return queuedVersion == null || queuedVersion === loadedTrip?.assignmentVersion;
      }));
    } catch { setPendingOps([]); }
  };
  useEffect(() => { void load(); }, [id]);

  useEffect(() => {
    const unsubscribe = onTripRefresh((event) => {
      const matches = !event.bookingId || event.bookingId === id || event.bookingId === trip?.id;
      if (!matches) return;
      if (event.reason === 'unassigned' || event.reason === 'cancelled') {
        if (id) void discardPendingMutationsForBooking(id);
        if (trip?.id) void discardPendingMutationsForBooking(trip.id);
        navigate('/today', { replace: true });
        return;
      }
      void load(true);
    });
    const refreshVisible = () => { if (document.visibilityState === 'visible') void load(true); };
    document.addEventListener('visibilitychange', refreshVisible);
    window.addEventListener('focus', refreshVisible);
    const timer = window.setInterval(refreshVisible, 20_000);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', refreshVisible);
      window.removeEventListener('focus', refreshVisible);
      window.clearInterval(timer);
    };
  }, [id, trip?.id, navigate]);

  // Geocode pickup + drop addresses when the booking doesn't carry coords.
  // geocodeAddress() caches in localStorage so repeat views are instant.
  useEffect(() => {
    if (!trip) return;
    const hasPickupCoords = typeof trip.pickupLat === 'number' && typeof trip.pickupLng === 'number';
    if (!hasPickupCoords && trip.pickupLocation) {
      void geocodeAddress(trip.pickupLocation).then(setGeocodedPickup);
    } else {
      setGeocodedPickup(null);
    }
    const hasDropCoords = typeof trip.dropoffLat === 'number' && typeof trip.dropoffLng === 'number';
    if (!hasDropCoords && trip.dropLocation) {
      void geocodeAddress(trip.dropLocation).then(setGeocodedDrop);
    } else {
      setGeocodedDrop(null);
    }
  }, [trip?.id, trip?.pickupLocation, trip?.dropLocation, trip?.pickupLat, trip?.pickupLng, trip?.dropoffLat, trip?.dropoffLng]);

  const handleStart = async (data: { startKm: number | null; actualStartDate: string; actualStartTime: string }) => {
    if (!id) return;
    setStartError(null);
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/start`, {
        method: 'POST',
        // startKm is optional — omit the key entirely when skipped (the strict
        // backend schema accepts absent, not null).
        body: {
          assignmentVersion: trip?.assignmentVersion,
          ...(data.startKm != null ? { startKm: data.startKm } : {}),
          actualStartDate: data.actualStartDate,
          actualStartTime: data.actualStartTime,
        },
        op: 'start-trip',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today'],
      });
      haptic.success();
      setActiveSheet(null);
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e: any) {
      haptic.warn();
      setStartError(e?.message || t('start_trip_failed', { defaultValue: 'Could not start the trip. Refresh and try again.' }));
      if (e?.status === 403 || e?.status === 409) void load(true);
      // eslint-disable-next-line no-console
      console.error('[TripDetail] start trip failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  type ReleaseSignoff =
    | { passengerSignature: string; requesterRating: number; signedOn: string; dutySlipUrl?: string | null }
    | { signatureSkippedReason: string };

  const handleRelease = async (signoff: ReleaseSignoff) => {
    if (!id) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/end`, {
        method: 'POST',
        body: { assignmentVersion: trip?.assignmentVersion, ...releaseDraft, ...signoff },
        op: 'end-trip',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today', '/driver-app/me/trips/history'],
      });
      setActiveSheet(null);
      setReleaseDraft(null);
      // Stay on the trip — the post-signature close-out (release KM + location)
      // is the next step and renders on this screen once status is RELEASED.
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] release trip failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReleaseReading = async (data: { releaseKm: number; releasedLocation: string }) => {
    if (!id) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/release-reading`, {
        method: 'POST',
        body: {
          assignmentVersion: trip?.assignmentVersion,
          releaseKm: data.releaseKm,
          ...(data.releasedLocation ? { releasedLocation: data.releasedLocation } : {}),
        },
        op: 'release-reading',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today', '/driver-app/me/trips/history'],
      });
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
      // Continue the close-out immediately into the real garage-in End KM
      // capture, where the duty-slip photo now belongs.
      setActiveSheet('end-reading');
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] release reading failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartKm = async (startKm: number) => {
    if (!id) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/start-km`, {
        method: 'POST',
        body: { assignmentVersion: trip?.assignmentVersion, startKm },
        op: 'start-km',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today'],
      });
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] start km failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecapture = async (signoff: ReleaseSignoff) => {
    if (!id) return;
    if (!('passengerSignature' in signoff)) return; // recapture is always the signed variant; satisfy the union
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/duty-slip`, {
        method: 'POST',
        body: {
          assignmentVersion: trip?.assignmentVersion,
          passengerSignature: signoff.passengerSignature,
          requesterRating: signoff.requesterRating,
          signedOn: signoff.signedOn,
          dutySlipUrl: signoff.dutySlipUrl ?? null,
        },
        op: 'submit-duty-slip',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today', '/driver-app/me/trips/history'],
      });
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] recapture duty slip failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndReading = async (data: { endDate: string; endTime: string; endKm: number; dutySlipUrl: string | null }) => {
    if (!id) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/end-reading`, {
        method: 'POST',
        body: { assignmentVersion: trip?.assignmentVersion, ...data },
        op: 'end-reading',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today', '/driver-app/me/trips/history'],
      });
      haptic.success();
      setActiveSheet(null);
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] end reading failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTripEvent = async (type: 'REACHED_LOCATION' | 'RIDE_STARTED', at: string) => {
    if (!id) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/trip-event`, {
        method: 'POST', body: { assignmentVersion: trip?.assignmentVersion, type, at }, op: 'trip-event',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, '/driver-app/me/trips/today'],
      });
      setActiveSheet(null);
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] trip-event failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddExpense = async (data: {
    type: string; amount: number; date: string;
    description: string | null; paymentSource: string; receiptUrl: string | null;
  }): Promise<boolean> => {
    if (!id) return false;
    setSubmitting(true);
    try {
      const result = await mutateOrQueue<{ expense: NonNullable<TripDetail['expenses']>[number] }>(`/driver-app/bookings/${id}/expenses`, {
        method: 'POST',
        body: data,
        op: 'add-expense',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, `/driver-app/bookings/${id}/expenses`],
      });
      const expense = result.queued
        ? { id: `pending-${result.queueItemId}`, ...data, pendingSync: true }
        : result.data.expense;
      setTrip((current) => current ? { ...current, expenses: [expense, ...(current.expenses ?? [])] } : current);
      return true;
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] add expense failed', e);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditExpense = async (data: {
    type: string; amount: number; date: string;
    description: string | null; paymentSource: string; receiptUrl: string | null;
  }) => {
    if (!id || !editingExpense) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/expenses/${editingExpense.id}`, {
        method: 'PATCH',
        body: data,
        op: 'edit-expense',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, `/driver-app/bookings/${id}/expenses`],
      });
      setActiveSheet(null);
      setEditingExpense(null);
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] edit expense failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!id || !editingExpense) return;
    setSubmitting(true);
    try {
      await mutateOrQueue(`/driver-app/bookings/${id}/expenses/${editingExpense.id}`, {
        method: 'DELETE',
        op: 'delete-expense',
        invalidatesOnSuccess: [`/driver-app/me/trips/${id}`, `/driver-app/bookings/${id}/expenses`],
      });
      setActiveSheet(null);
      setEditingExpense(null);
      await invalidate(`/driver-app/me/trips/${id}`);
      await load();
    } catch (e) {
      haptic.warn();
      // eslint-disable-next-line no-console
      console.error('[TripDetail] delete expense failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  // Drive the global AppHeader for this route: hamburger → back chevron + hide
  // bell. Title is intentionally omitted — the page body owns the headline.
  // The hook clears the override on unmount so other routes get default chrome.
  useSetPageHeader({ backTo: '/', hideBell: true }, []);

  // ---- Unsynced-mutation overlay ----------------------------------------
  // Queued (offline / flaky-network) writes are durable in IDB before the
  // server has them. Overlay them onto the server state so the driver's
  // progress survives an app restart instead of appearing lost.
  const pendingOp = (op: QueueItem['op']) => pendingOps.find(p => p.op === op);
  const pendingEventAt = (type: 'REACHED_LOCATION' | 'RIDE_STARTED'): string | null => {
    const item = pendingOps.find(p => p.op === 'trip-event' && (p.body as { type?: string } | undefined)?.type === type);
    return item ? ((item.body as { at?: string }).at ?? null) : null;
  };
  const pendingStartBody = pendingOp('start-trip')?.body as
    | { startKm?: number; actualStartTime?: string }
    | undefined;
  const serverStatus = trip?.status?.toUpperCase() ?? '';
  const effStatus =
    serverStatus === 'ALLOCATED' && pendingOp('start-trip')
      ? 'DISPATCHED'
      : (serverStatus === 'DISPATCHED' || serverStatus === 'ONGOING') && pendingOp('end-trip')
        ? 'RELEASED'
        : serverStatus;
  const hasPendingSync = pendingOps.length > 0;

  // In-flight (DISPATCHED/ONGOING): keep the screen awake while the driver is on
  // the trip, and (below) reserve extra bottom clearance for the tall action bar.
  const inFlight = effStatus === 'DISPATCHED' || effStatus === 'ONGOING';
  useWakeLock(inFlight);

  if (loading && !trip) return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  if (error && !trip) return <ErrorState onRetry={load} />;
  if (!trip) return null;

  // Bookings rarely have lat/lng — ops creates them with addresses only. We
  // render the map from EITHER the API coords (if present) OR coords we
  // geocoded client-side from the address. The "effective" values fall back
  // through this chain so the rest of the render is uniform.
  const apiPickup = (typeof trip.pickupLat === 'number' && typeof trip.pickupLng === 'number')
    ? { lat: trip.pickupLat, lng: trip.pickupLng } as GeoCoords
    : null;
  const apiDrop = (typeof trip.dropoffLat === 'number' && typeof trip.dropoffLng === 'number')
    ? { lat: trip.dropoffLat, lng: trip.dropoffLng } as GeoCoords
    : null;
  const effectivePickup = apiPickup ?? geocodedPickup;
  const effectiveDrop = apiDrop ?? geocodedDrop;
  const hasMap = !!effectivePickup;
  const hasPickupAddress = !!(trip.pickupLocation && trip.pickupLocation.trim());
  const hasDropAddress = !!(trip.dropLocation && trip.dropLocation.trim());

  const noteText = trip.remarks ?? null;

  return (
    <div
      style={{
        paddingBottom: inFlight
          ? 'calc(200px + env(safe-area-inset-bottom))'
          : 'calc(var(--bottom-bar-height) + env(safe-area-inset-bottom))',
      }}
    >
      {stale && fetchedAt ? <StaleBanner fetchedAt={fetchedAt} onRefresh={load} /> : null}
      {hasPendingSync ? (
        <div
          className="mx-6 mb-3 flex items-center gap-2 px-3.5 py-2.5 rounded-[12px] text-[13px] font-semibold"
          style={{ background: 'var(--mint-soft)', color: 'var(--green-700)', border: '1px solid var(--mint-line)' }}
          role="status"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
          {t('pendingSync', { defaultValue: 'Saved on this phone — will sync automatically.' })}
        </div>
      ) : null}
      {/* Page chrome (back arrow + title) lives in the AppHeader — driven by
          useSetPageHeader above. The body starts directly with the map (or
          the no-map status row) per v2 trip-active spec. */}

      {/* Map hero — only rendered when we have pickup coords. Bookings in
          this system rarely carry lat/lng (ops creates them with addresses
          only), so most trips skip the map entirely. The Route card below
          owns the location surface in that case; status pill moves into a
          small standalone row beneath the title.
          When the map IS shown, the Open-in-Maps + status pill overlays
          remain per the v2 mockup. */}
      {hasMap && effectivePickup ? (
        <div className="mx-6 mb-4" style={{ position: 'relative' }}>
          <MapHero pickup={effectivePickup} drop={effectiveDrop} />
          <div
            className="flex items-center gap-2"
            style={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              zIndex: 1000,
            }}
          >
            <StatusBadge status={trip.status} />
            {trip.code ? (
              <span
                className="text-xs font-semibold rounded-full px-2.5 py-1"
                style={{
                  background: 'rgba(26, 26, 26, 0.92)',
                  color: '#FFFFFF',
                  letterSpacing: '0.04em',
                }}
              >
                {trip.code}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        // No map: status pill + booking code share a row so the driver still
        // sees the trip state above the cards AND can read out the code when
        // talking to ops. The Route card carries the address.
        <div className="mx-6 mb-4 flex items-center gap-2">
          <StatusBadge status={trip.status} />
          {trip.code ? (
            <span
              className="text-xs font-semibold tracking-wider text-[var(--color-text-secondary)]"
              style={{ letterSpacing: '0.04em' }}
            >
              {trip.code}
            </span>
          ) : null}
        </div>
      )}

      {/* Trip log — 4-cell grid per v2 active-trip spec. Shows captured values
          inline (Garage / Start KM / Release / Release KM) so the driver can
          see what they entered without re-opening a sheet. Garage reads the
          start-trip time; Release + Release KM read the release reading. */}
      {(() => {
        const status = effStatus;
        if (status !== 'DISPATCHED' && status !== 'ONGOING' && status !== 'COMPLETED' && status !== 'RELEASED') return null;
        const effStartTimeRaw = trip.actualStartTime ?? pendingStartBody?.actualStartTime ?? null;
        const startTime = effStartTimeRaw && effStartTimeRaw.length >= 5
          ? effStartTimeRaw.slice(0, 5)
          : null;
        const releaseTime = trip.releaseTime && trip.releaseTime.length >= 5
          ? trip.releaseTime.slice(0, 5)
          : null;
        const effStartKm = trip.startKm ?? pendingStartBody?.startKm ?? null;
        const cells: { label: string; value: string | null }[] = [
          { label: t('tripLog.garage',    { defaultValue: 'Garage' }),     value: startTime },
          { label: t('tripLog.startKm',   { defaultValue: 'Start KM' }),   value: effStartKm != null ? effStartKm.toLocaleString('en-IN') : null },
          { label: t('tripLog.release',   { defaultValue: 'Release' }),    value: releaseTime },
          { label: t('tripLog.releaseKm', { defaultValue: 'Release KM' }), value: trip.releaseKm != null ? trip.releaseKm.toLocaleString('en-IN') : null },
        ];
        return (
          <div className="mx-6 mb-4">
            <h2 className="text-[11px] font-bold uppercase mb-2 px-1" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.10em' }}>
              {t('tripLog.title', { defaultValue: 'Trip log' })}
            </h2>
            <div className="grid grid-cols-4 gap-2">
              {cells.map(cell => {
                const filled = cell.value != null;
                return (
                  <div
                    key={cell.label}
                    style={{
                      padding: '10px 6px 12px',
                      background: filled ? 'var(--mint-soft)' : 'var(--color-surface)',
                      border: `1px solid ${filled ? 'var(--mint-line)' : 'var(--color-border)'}`,
                      borderRadius: 12,
                      textAlign: 'center',
                      transition: 'background 240ms ease, border-color 240ms ease',
                    }}
                  >
                    <div
                      className="font-bold uppercase mb-1"
                      style={{
                        fontSize: 9,
                        color: filled ? 'var(--green-700)' : 'var(--color-text-dim,#9AA3AC)',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {cell.label}
                    </div>
                    <div
                      className="font-extrabold"
                      style={{
                        fontSize: 17,
                        letterSpacing: '-0.01em',
                        color: filled ? 'var(--color-text)' : 'var(--color-text-dim,#9AA3AC)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {cell.value ?? '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* POST-SIGNATURE CLOSE-OUT — release KM (required) + location, entered
          after the passenger signed and left. Only capture point for the KM
          (the signature screen shows times only), so it leads the page. */}
      {effStatus === 'RELEASED' && trip.releaseKm == null && !pendingOp('release-reading') ? (
        <ReleaseReadingCard
          startKm={trip.startKm ?? pendingStartBody?.startKm ?? null}
          submitting={submitting}
          onSave={(d) => { void handleReleaseReading(d); }}
        />
      ) : null}

      {/* START-KM BACKFILL — start allows skipping the odometer; this keeps the
          gap visible + fixable mid-trip instead of dead-ending at completion. */}
      {(effStatus === 'DISPATCHED' || effStatus === 'ONGOING')
        && trip.startKm == null && pendingStartBody?.startKm == null && !pendingOp('start-km') ? (
        <StartKmBanner submitting={submitting} onSave={(km) => { void handleStartKm(km); }} />
      ) : null}

      {/* PASSENGER — v2 spec: section eyebrow + card with name + Fraunces italic
          client/company + divider + phone with green Call pill. Skipped when no
          passengers are on the booking. Iterates over all passengers in case
          the booking has more than one rider. */}
      {trip.passengers && trip.passengers.length > 0 ? (
        <div className="mx-6 mb-4">
          <h2 className="text-[11px] font-bold uppercase mb-2 px-1" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
            {t('passenger', { count: trip.passengers.length })}
          </h2>
          <Card>
            {trip.passengers.map((p, idx) => (
              <div key={p.id} className={idx > 0 ? 'mt-3 pt-3 border-t border-[var(--color-border)]' : ''}>
                <div className="font-extrabold text-[18px] tracking-tight text-[var(--color-text)]">{p.name}</div>
                {trip.client?.name && idx === 0 ? (
                  <div className="text-[15px] text-[var(--color-text-secondary)] mt-0.5">
                    <span className="serif-accent text-[16px]">{trip.client.name}</span>
                  </div>
                ) : null}
                <hr className="my-3 border-t border-[var(--color-border)]" />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-[var(--color-text-muted,#6B7480)] tabular-nums">{p.phone || '—'}</span>
                  {p.phone ? (
                    <a
                      href={`tel:${p.phone}`}
                      onClick={() => haptic.tap()}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold"
                      style={{
                        background: 'var(--mint-soft)',
                        color: 'var(--green-700)',
                        border: '1px solid var(--mint-line)',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {t('callPassenger', { defaultValue: 'Call' })}
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </Card>
        </div>
      ) : null}

      {/* ROUTE — pickup → drop with green-dot + black-square markers and a
          dotted rail, then a meta row with duty type / car type. The route
          card is always shown so drivers always see pickup time even when
          addresses are missing (Mehatab-style trips). */}
      <div className="mx-6 mb-4">
        <h2 className="text-[11px] font-bold uppercase mb-2 px-1" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
          {t('route', { defaultValue: 'Route' })}
        </h2>
        <Card>
          <div className="flex items-start justify-between gap-2 mb-3.5">
            <span className="text-[11px] font-bold uppercase" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
              {t('pickupTime')}
            </span>
            <span className="text-[14px] font-bold text-[var(--color-text)] tracking-tight">
              {formatISTDateTime(trip.pickupTime)}
            </span>
          </div>
          <div className="flex gap-3">
            {/* Marker rail — green dot always; dotted line + black square only
                when there's a drop address to anchor the second marker. */}
            <div className="flex flex-col items-center pt-1" aria-hidden>
              <span style={{
                width: 12, height: 12, borderRadius: 999,
                background: 'var(--color-primary)',
                boxShadow: '0 0 0 3px var(--mint-soft)',
              }} />
              {hasDropAddress ? (
                <>
                  <span style={{
                    width: 2,
                    flex: 1,
                    minHeight: 26,
                    marginTop: 4,
                    marginBottom: 4,
                    backgroundImage: 'linear-gradient(var(--color-text-dim,#9AA3AC) 50%, transparent 50%)',
                    backgroundSize: '2px 6px',
                    backgroundRepeat: 'repeat-y',
                    opacity: 0.55,
                  }} />
                  <span style={{
                    width: 10, height: 10,
                    background: 'var(--color-text)',
                    borderRadius: 2,
                  }} />
                </>
              ) : null}
            </div>
            <div className="flex-1 flex flex-col gap-3.5">
              <div>
                <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
                  {t('pickup', { defaultValue: 'Pickup' })}
                </div>
                <SelectableText
                  value={trip.pickupLocation || '—'}
                  className="text-[14px] font-semibold text-[var(--color-text)] mt-0.5"
                />
                {(trip.flightNo?.trim() || trip.trainNo?.trim()) ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                    {trip.flightNo?.trim() ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 8, background: '#E5EEFF', color: '#2563EB', border: '1px solid #CFE0FF' }}>
                        ✈ {t('flight', { defaultValue: 'Flight' })} {trip.flightNo.trim()}
                      </span>
                    ) : null}
                    {trip.trainNo?.trim() ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 8, background: 'var(--mint-soft)', color: 'var(--green-700)', border: '1px solid var(--mint-line,#CFE6D8)' }}>
                        🚆 {t('train', { defaultValue: 'Train' })} {trip.trainNo.trim()}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {hasDropAddress ? (
                <div>
                  <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
                    {t('drop', { defaultValue: 'Drop' })}
                  </div>
                  <SelectableText
                    value={trip.dropLocation!}
                    className="text-[14px] font-semibold text-[var(--color-text)] mt-0.5"
                  />
                </div>
              ) : null}
            </div>
          </div>
          {/* Meta row — only when there's any metadata to show */}
          {(trip.dutyType || trip.requestedCarType) ? (
            <div className="flex gap-4 mt-4 pt-3.5 border-t border-[var(--color-border)] text-[13px] text-[var(--color-text-muted,#6B7480)] flex-wrap">
              {trip.dutyType ? (
                <span><strong className="text-[var(--color-text)] font-bold mr-1">{trip.dutyType}</strong></span>
              ) : null}
              {trip.requestedCarType ? (
                <span><strong className="text-[var(--color-text)] font-bold">{trip.requestedCarType}</strong></span>
              ) : null}
            </div>
          ) : null}
        </Card>
      </div>

      {/* REMARKS — operator-provided text under the `Booking.remarks` field.
          Rendered as a read-only textarea so drivers can long-press to select
          + copy on iOS/Android. Skipped when empty. */}
      {noteText ? (
        <div className="mx-6 mb-4">
          <h2 className="text-[11px] font-bold uppercase mb-2 px-1" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
            {t('notes')}
          </h2>
          <Card>
            <SelectableText
              value={noteText}
              className="text-sm text-[var(--color-text)] leading-relaxed whitespace-pre-line"
            />
          </Card>
        </div>
      ) : null}

      {/* VEHICLE — mint icon tile + reg + model per v2 spec. Skipped when no
          vehicle assigned (rare — usually assigned at allocation). Placed below
          Route + Remarks so the driver sees the actionable route/notes first;
          the vehicle (their own car) is reference info. */}
      {trip.activeVehicle ? (
        <div className="mx-6 mb-4">
          <h2 className="text-[11px] font-bold uppercase mb-2 px-1" style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}>
            {t('vehicle')}
          </h2>
          <Card>
            <div className="flex items-center gap-3.5">
              <div
                aria-hidden
                style={{
                  width: 44, height: 44,
                  borderRadius: 12,
                  background: 'var(--mint-soft)',
                  color: 'var(--green-700)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V15a1 1 0 0 0 1 1h2" />
                  <circle cx="6.5" cy="16.5" r="2.5" />
                  <circle cx="16.5" cy="16.5" r="2.5" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-[16px] tabular-nums tracking-wide text-[var(--color-text)]">
                  {trip.activeVehicle.regNumber}
                </div>
                <div className="text-[13px] text-[var(--color-text-muted,#6B7480)] mt-0.5">
                  {trip.activeVehicle.make} {trip.activeVehicle.model}
                </div>
              </div>
            </div>
          </Card>
        </div>
      ) : null}

      <Card className="mx-6 mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] mb-3">{t('expenses')}</h2>
        {trip.expenses && trip.expenses.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {trip.expenses.map(e => {
              // Editable rows are tappable → edit. FASTag/auto-imported rows
              // (externalProvider) stay read-only even inside the window.
              const rowEditable = !!trip.expensesEditable && !e.externalProvider;
              const inner = (
                <>
                  <span className="min-w-0 text-left">
                    <span className="block text-[var(--color-text)]">{e.label || e.type}</span>
                    {e.pendingSync || e.reviewStatus === 'PENDING' ? (
                      <span className="block mt-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--status-warning-fg,#8A5E1D)' }}>
                        {e.pendingSync
                          ? t('expense_sync_pending', { defaultValue: 'Waiting to sync' })
                          : t('expense_review_pending', { defaultValue: 'Awaiting approval' })}
                      </span>
                    ) : e.reviewStatus === 'REJECTED' ? (
                      <span className="block mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-danger,#C13834)]">
                        {t('expense_rejected', { defaultValue: 'Rejected — tap to edit' })}
                      </span>
                    ) : e.reviewStatus === 'APPROVED' ? (
                      <span className="block mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--green-700)]">
                        {t('expense_approved', { defaultValue: 'Approved' })}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex items-center gap-1.5 text-[var(--color-text)] font-semibold">
                    {formatINR(e.amount)}
                    {rowEditable ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    ) : null}
                  </span>
                </>
              );
              return rowEditable ? (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => { haptic.tap(); setEditingExpense(e); setActiveSheet('expense'); }}
                    className="w-full flex justify-between items-center active:opacity-60"
                  >
                    {inner}
                  </button>
                </li>
              ) : (
                <li key={e.id} className="flex justify-between items-center">{inner}</li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('noExpensesYet')}</p>
        )}
        {/* Add / locked footer. `expensesEditable` is the backend window flag —
            editable while the trip is live and for 24h after release, then
            locked for billing. */}
        {(() => {
          // Only offer "Add Expense" once the trip has actually started.
          // Before that (UNASSIGNED / ALLOCATED) there's nothing to log, so the
          // button is hidden and we show the "once the trip starts" hint — even
          // though the backend `expensesEditable` window flag may already be true.
          const started = ['DISPATCHED', 'ONGOING', 'RELEASED', 'COMPLETED', 'BILLING_PENDING', 'BILLED']
            .includes(effStatus);
          if (started && trip.expensesEditable) {
            return (
              <button
                type="button"
                onClick={() => { haptic.tap(); setEditingExpense(null); setActiveSheet('expense'); }}
                className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-[12px] text-sm font-bold transition-colors active:bg-[var(--mint-soft)]"
                style={{
                  border: '1.5px dashed rgba(46, 158, 101, 0.5)',
                  color: 'var(--green-700)',
                  background: 'transparent',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8.5v7M8.5 12h7" />
                </svg>
                {t('add_expense', { defaultValue: 'Add Expense' })}
              </button>
            );
          }
          // No add button: explain why (not started yet, vs locked post-window).
          return (
            <p className="mt-3 text-xs text-[var(--color-text-secondary)]">
              {!started
                ? t('expenses_after_start', { defaultValue: 'You can log expenses once the trip starts.' })
                : t('expenses_locked', { defaultValue: 'Expenses are locked — they can be changed up to 24h after release.' })}
            </p>
          );
        })()}
      </Card>

      {/* Duty Slip card — post-release re-capture path. Shown only when the
          trip is RELEASED or COMPLETED and the sign-off hasn't been captured
          yet. The release flow (slide-to-release) handles the primary path;
          this card is the fallback for drivers who skipped or need to redo. */}
      {(() => {
        const status = effStatus;
        if ((status !== 'RELEASED' && status !== 'COMPLETED') || !!trip.signedOn) return null;
        return (
          <Card className="mx-6 mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] mb-3">
              {t('duty_slip_title', { defaultValue: 'Duty Slip' })}
            </h2>
            <p className="text-sm text-[var(--color-text-secondary)] mb-3">
              {t('signoff_pending', { defaultValue: 'Passenger sign-off not captured yet' })}
            </p>
            <button
              type="button"
              onClick={() => { haptic.tap(); setActiveSheet('dutyslip'); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[12px] text-sm font-bold transition-colors active:bg-[var(--mint-soft)]"
              style={{
                border: '1.5px dashed rgba(46, 158, 101, 0.5)',
                color: 'var(--green-700)',
                background: 'transparent',
              }}
            >
              {t('signoff_capture', { defaultValue: 'Capture sign-off' })}
            </button>
          </Card>
        );
      })()}

      {/* End Reading card — garage-in / end-of-duty capture, shown after release.
          The release flow defaults End = Release; this lets the driver record the
          real garage-in (the return leg) so the operator's Final Reporting "End"
          row, the duty slip, and garage-to-garage billing get the true end. */}
      {(() => {
        const status = effStatus;
        if (!['RELEASED', 'COMPLETED', 'BILLING_PENDING', 'BILLED'].includes(status)) return null;
        const endTime = trip.actualEndTime && trip.actualEndTime.length >= 5 ? trip.actualEndTime.slice(0, 5) : null;
        const hasEnd = trip.endKm != null || endTime != null;
        const endCells = [
          { label: t('tripLog.end', { defaultValue: 'End' }), value: endTime },
          { label: t('end_date_label', { defaultValue: 'End date' }), value: trip.actualEndDate ? formatISTDate(trip.actualEndDate) : null },
          { label: t('tripLog.endKm', { defaultValue: 'End KM' }), value: trip.endKm != null ? trip.endKm.toLocaleString('en-IN') : null },
        ];
        return (
          <Card className="mx-6 mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] mb-3">
              {t('end_reading_title', { defaultValue: 'End reading (garage in)' })}
            </h2>
            {hasEnd ? (
              <div className="grid grid-cols-3 gap-2">
                {endCells.map(c => (
                  <div
                    key={c.label}
                    style={{ padding: '10px 6px 12px', background: 'var(--mint-soft)', border: '1px solid var(--mint-line)', borderRadius: 12, textAlign: 'center' }}
                  >
                    <div className="font-bold uppercase mb-1" style={{ fontSize: 9, color: 'var(--green-700)', letterSpacing: '0.06em' }}>
                      {c.label}
                    </div>
                    <div className="font-extrabold" style={{ fontSize: 15, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
                      {c.value ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">
                {t('end_reading_none', { defaultValue: 'Garage-in reading not captured yet.' })}
              </p>
            )}
            {trip.endReadingEditable ? (
              <button
                type="button"
                onClick={() => { haptic.tap(); setActiveSheet('end-reading'); }}
                className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-[12px] text-sm font-bold transition-colors active:bg-[var(--mint-soft)]"
                style={{ border: '1.5px dashed rgba(46, 158, 101, 0.5)', color: 'var(--green-700)', background: 'transparent' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8.5v7M8.5 12h7" />
                </svg>
                {hasEnd
                  ? t('end_reading_edit', { defaultValue: 'Edit end reading' })
                  : t('end_reading_add', { defaultValue: 'Add end reading' })}
              </button>
            ) : (
              <p className="mt-3 text-xs text-[var(--color-text-secondary)]">
                {t('end_reading_locked', { defaultValue: 'End reading is locked — it can be changed up to 24h after release.' })}
              </p>
            )}
          </Card>
        );
      })()}

      {/* Status-aware bottom action bar */}
      {(() => {
        const status = effStatus;
        let content: React.ReactNode = null;

        if (status === 'ALLOCATED') {
          // The previous slider only opened another confirmation sheet, which
          // looked like a committed start and then reset after 600ms. Use one
          // explicit entry point; the sheet's final button owns the API write.
          content = (
            <Button className="w-full" onClick={() => { setStartError(null); setActiveSheet('start'); }}>
              {t('start_trip_confirm', { defaultValue: 'Start trip' })}
            </Button>
          );
        } else if (status === 'DISPATCHED' || status === 'ONGOING') {
          // Trip is in-flight. Reached + Picked-up are ONE-TAP events — each
          // tap records the device time immediately (2026-07-02: no date/time
          // popup; ops corrects outliers). Slide-to-release captures the
          // release TIME and opens the customer signature screen; the KM +
          // location follow on the post-signature close-out.
          const tm = trip.tripMeta;
          content = (
            <TripProgressBar
              reachedAt={tm?.reachedLocationAt ?? pendingEventAt('REACHED_LOCATION')}
              startedAt={tm?.rideStartedAt ?? pendingEventAt('RIDE_STARTED')}
              submitting={submitting}
              onReached={() => { haptic.tap(); void handleTripEvent('REACHED_LOCATION', new Date().toISOString()); }}
              onStartRide={() => { haptic.tap(); void handleTripEvent('RIDE_STARTED', new Date().toISOString()); }}
              onRelease={() => {
                setReleaseDraft({ releaseDate: istDateYmd(new Date()), releaseTime: formatISTTime(new Date()) });
                setActiveSheet('dutyslip-release');
              }}
            />
          );
        } else if (status === 'BILLED' || status === 'BILLED_AND_PAID') {
          content = (
            <Link to={`/duty-slip-view/${trip.code || trip.id}`} className="block">
              <Button className="w-full" variant="secondary">
                {t('viewDutySlip', { defaultValue: 'View Duty Slip' })}
              </Button>
            </Link>
          );
        }

        if (!content) return null;
        return (
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              maxWidth: 440,
              zIndex: 20,
              background: 'var(--color-surface)',
              borderTop: '1px solid var(--color-border)',
              padding: '14px 18px calc(14px + env(safe-area-inset-bottom))',
              boxShadow: 'var(--bottom-bar-shadow)',
            }}
          >
            {content}
          </div>
        );
      })()}

      <StartTripSheet
        open={activeSheet === 'start'}
        onClose={() => { if (!submitting) { setActiveSheet(null); setStartError(null); } }}
        onConfirm={handleStart}
        submitting={submitting}
        error={startError}
      />
      <ExpenseSheet
        key={editingExpense?.id ?? 'add'}
        open={activeSheet === 'expense'}
        minDate={istDateYmd(trip.pickupTime)}
        mode={editingExpense ? 'edit' : 'add'}
        initial={editingExpense ? {
          type: editingExpense.type as ExpenseDraft['type'],
          amount: editingExpense.amount,
          date: (editingExpense.date ?? '').slice(0, 10) || undefined,
          description: editingExpense.description ?? null,
          paymentSource: (editingExpense.paymentSource ?? 'DRIVER') as ExpenseDraft['paymentSource'],
          receiptUrl: editingExpense.receiptUrl ?? null,
        } : null}
        onClose={() => { setActiveSheet(null); setEditingExpense(null); }}
        onConfirm={editingExpense ? handleEditExpense : handleAddExpense}
        onDelete={editingExpense ? handleDeleteExpense : undefined}
      />
      {/* Release path: slide-to-release stamps the release TIME into the draft
          and opens the customer signature screen (times only, no KM). onSubmit
          fires ONE /end call with the time + sign-off; the odometer follows on
          the post-signature close-out card. */}
      {activeSheet === 'dutyslip-release' && releaseDraft ? (
        <ReleaseDutySlipSheet
          open
          bookingId={trip.code || trip.id}
          mode="release"
          journey={{ pickupAt: trip.tripMeta?.rideStartedAt ?? null, releaseTime: releaseDraft.releaseTime }}
          onSubmit={handleRelease}
          onClose={() => { setActiveSheet(null); setReleaseDraft(null); }}
          onSubmitted={() => {}}
        />
      ) : null}
      {/* Recapture path: opened from the standalone duty-slip card for
          RELEASED/COMPLETED trips that don't yet have a sign-off. Posts to
          /duty-slip (not /end). */}
      <ReleaseDutySlipSheet
        open={activeSheet === 'dutyslip'}
        bookingId={trip.code || trip.id}
        mode="recapture"
        journey={{ pickupAt: trip.tripMeta?.rideStartedAt ?? null, releaseTime: trip.releaseTime ?? '' }}
        onSubmit={handleRecapture}
        onClose={() => setActiveSheet(null)}
        onSubmitted={() => { void load(); }}
      />

      {/* End-reading path: garage-in capture for a released trip. Conditionally
          rendered so the sheet seeds its fields from the release/end props each
          time it opens. Release KM may still be pending (close-out) — the sheet
          handles a null floor. */}
      {activeSheet === 'end-reading' && trip.releaseDate && trip.releaseTime ? (
        <EndReadingSheet
          open
          release={{ date: trip.releaseDate, time: trip.releaseTime.slice(0, 5), km: trip.releaseKm ?? null }}
          initial={{ date: trip.actualEndDate ?? null, time: trip.actualEndTime ?? null, km: trip.endKm ?? null, dutySlipUrl: trip.dutySlipUrl ?? null }}
          onClose={() => { if (!submitting) setActiveSheet(null); }}
          onConfirm={handleEndReading}
          submitting={submitting}
        />
      ) : null}
    </div>
  );
}
